/**
 * AudienceInsightsPage — Measure pillar analytics surface.
 *
 * Read-only view of every marketing audience (system + custom): size,
 * reachability and last-refresh recency. Deep-links to the Marketing
 * Audiences management surface for edits.
 *
 * Data source:
 *   - GET /api/admin/email-analytics/audiences → { audiences: [...] }
 *
 * Mirrors MeasureOverviewPage data-fetch / loading / error conventions.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import PillarPageShell from "../shared/PillarPageShell";
import apiService from "../../../services/api";

interface AudienceRow {
  id: string;
  slug: string;
  name: string;
  is_system: boolean;
  last_count: number | null;
  last_refreshed_at: string | null;
  member_count: number | null;
}

const NAVY = "#1A5F7A";
const SLATE = "#5D6D7E";
const GOLD = "#D4AC0D";
const TEAL = "#146152";

/** Format an integer with thousands separators, or em-dash when null. */
const fmtNum = (n: number | null | undefined): string =>
  n != null && Number.isFinite(n) ? n.toLocaleString() : "—";

/** Format an ISO timestamp into a short, friendly absolute date, or em-dash. */
const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/** Relative "freshness" label + color for a last-refresh timestamp. */
const freshness = (iso: string | null | undefined): { label: string; color: string } => {
  if (!iso) return { label: "Never", color: SLATE };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { label: "—", color: SLATE };
  const days = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
  if (days <= 1) return { label: "Fresh", color: TEAL };
  if (days <= 7) return { label: "Recent", color: NAVY };
  if (days <= 30) return { label: "Stale", color: GOLD };
  return { label: "Old", color: "#B23B3B" };
};

const KpiCard: React.FC<{
  label: string;
  value: string;
  caption?: string;
  accent?: string;
}> = ({ label, value, caption, accent = NAVY }) => (
  <Card sx={{ borderRadius: 2, borderLeft: `6px solid ${accent}`, height: "100%" }}>
    <CardContent>
      <Typography variant="overline" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ color: accent, mt: 1, fontWeight: 700, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {caption}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const AudienceInsightsPage: React.FC = () => {
  const navigate = useNavigate();
  const [audiences, setAudiences] = useState<AudienceRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const res: any = await apiService.get(
          "/api/admin/email-analytics/audiences",
        );
        if (!mounted) return;
        // fetchWithAuth unwraps { success, data } → res.data; the audiences
        // endpoint returns { success, audiences:[...] } so the array lives
        // under res.data.audiences. Fall back across the common shapes.
        const payload = res?.data ?? res;
        const list: AudienceRow[] =
          payload?.audiences ??
          payload?.data?.audiences ??
          (Array.isArray(payload) ? payload : []);
        setAudiences(Array.isArray(list) ? list : []);
        setError(null);
      } catch (e: any) {
        if (mounted) setError(e?.message ?? "Failed to load audiences");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const totalAudiences = audiences?.length ?? null;
  const systemCount = audiences?.filter((a) => a.is_system).length ?? null;
  const customCount =
    audiences != null ? audiences.length - (systemCount ?? 0) : null;
  const totalMembers =
    audiences != null
      ? audiences.reduce((sum, a) => sum + (a.member_count ?? 0), 0)
      : null;
  const largest =
    audiences != null && audiences.length > 0
      ? audiences.reduce((max, a) =>
          (a.member_count ?? 0) > (max.member_count ?? 0) ? a : max,
        )
      : null;

  // Sort by member_count desc for the table (nulls last), without mutating state.
  const rows = audiences
    ? [...audiences].sort((a, b) => (b.member_count ?? -1) - (a.member_count ?? -1))
    : [];

  return (
    <PillarPageShell
      pillar="measure"
      title="Audience Insights"
      subtitle="Size, reachability and refresh recency across every marketing audience."
      rightActions={
        <Button
          variant="contained"
          onClick={() => navigate("/dashboard/communications/marketing/audiences")}
          sx={{
            bgcolor: NAVY,
            "&:hover": { bgcolor: "#15506A" },
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Manage →
        </Button>
      }
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPI cards */}
      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Total Audiences"
              value={fmtNum(totalAudiences)}
              caption={
                systemCount != null && customCount != null
                  ? `${systemCount} system · ${customCount} custom`
                  : undefined
              }
              accent={NAVY}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Total Members"
              value={fmtNum(totalMembers)}
              caption="Sum of member counts"
              accent={TEAL}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="System Audiences"
              value={fmtNum(systemCount)}
              caption="Built-in segments"
              accent={SLATE}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              label="Largest Audience"
              value={fmtNum(largest?.member_count ?? null)}
              caption={largest?.name ?? undefined}
              accent={GOLD}
            />
          </Grid>
        </Grid>
      )}

      {/* Audience table */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            Audiences
          </Typography>
          <Divider sx={{ mt: 1, mb: 1 }} />

          {loading ? (
            <Box>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="text" height={44} />
              ))}
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No audiences yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Slug</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Members
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last Refreshed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((a) => {
                    const fresh = freshness(a.last_refreshed_at);
                    return (
                      <TableRow key={a.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {a.name || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {a.slug ? (
                            <Chip
                              label={a.slug}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.72rem",
                                color: SLATE,
                                borderColor: "rgba(0,0,0,0.18)",
                              }}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
                          >
                            {fmtNum(a.member_count)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={a.is_system ? "System" : "Custom"}
                            size="small"
                            sx={{
                              bgcolor: a.is_system ? "rgba(93,109,126,0.14)" : "rgba(26,95,122,0.12)",
                              color: a.is_system ? SLATE : NAVY,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: fresh.color,
                                flexShrink: 0,
                              }}
                            />
                            <Box>
                              <Typography variant="body2">
                                {fmtDate(a.last_refreshed_at)}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: fresh.color, fontWeight: 600 }}
                              >
                                {fresh.label}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </PillarPageShell>
  );
};

export default AudienceInsightsPage;
